<template>
  <span v-if="props.anchor" :id="props.anchor" class="anchor"></span>
  <div class="card" v-bind="$attrs">
    <div class="card-header d-flex justify-content-between bg-primary">
      <h4 class="text-uppercase mb-0">Transferts</h4>
      <button class="btn btn-link link-dark p-0 link-underline-opacity-0 link-underline-opacity-100-hover" @click="addTransfer()"><i class="fa-solid fa-plus pe-1"></i>Ajouter un transfer</button>
    </div>
    <div class="card-body">
      <div v-if="transfers.length === 0" class="text-muted">
        Aucun transfert
      </div>
      <div v-else v-for="(transfer,idx) in transfers" :key="transfer.key" class="card mb-3">
        <span :id="`transfers[${idx}]`" class="anchor"></span>
        <div class="card-header d-flex justify-content-between bg-primary bg-opacity-25">
          <h4 class="text-uppercase mb-0">{{transfer.value?.name.length > 0 ? transfer.value?.name : `Transfer ${idx + 1}`}}</h4>
          <button class="btn btn-link link-dark p-0" @click="removeTransfer(idx)"><i class="fa-regular fa-trash-can fa-lg"></i></button>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-6 col-lg-2">
              <SelectInputComponent
                :value-key="`transfers[${idx}].type`"
                label="Type"
                placeholder="Type"
                :required="true"
                class="mb-3"
                :options="Object.values(TravelDbTransferTypeEnum).map(value => ({ label: GetTransferTypeLabel(value), value }))"
              />
            </div>
            <div class="col-12 col-md-6 col-lg-2">
              <SelectInputComponent
                :value-key="`transfers[${idx}].direction`"
                label="Direction"
                placeholder="Direction"
                :required="true"
                class="mb-3"
                :options="Object.values(TravelDbTransferDirectionEnum).map(value => ({ label: GetTransferDirectionLabel(value), value }))"
              />
            </div>
            <div class="col-12 col-md-6 col-lg-6">
              <TextInputComponent
                :value-key="`transfers[${idx}].name`"
                label="Nom transfert"
                placeholder="Nom transfert"
                :required="true"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-md-6 col-lg-2">
              <NumberInputComponent
                :value-key="`transfers[${idx}].quota`"
                label="Quota"
                placeholder="Quota"
                :required="true"
                class="mb-3"
              />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-12 col-md-6 col-lg-4 mb-sm-3 mb-md-0">
              <DatePickerComponent
                :value-key="`transfers[${idx}].startDate`"
                label="Date de départ"
                placeholder="Date de départ"
                :required="false"
                :flow="['year', 'month', 'calendar','time']"
                :enableTimePicker="true"
                :format="'dd/MM/yyyy HH:mm'"
                :minutes-increment="15"
                :autoApply="false"
                :min-date="travel.startDate"
              />
            </div>
            <div class="col-12 col-md-6 col-lg-8">
              <TextInputComponent
                :value-key="`transfers[${idx}].meetingPoint`"
                label="Lieu de rendez-vous"
                placeholder="Lieu de rendez-vous"
                :required="false"
              />
            </div>
          </div>

          <div class="row mb-3">
            <div class="col">
              <TextInputComponent
                :value-key="`transfers[${idx}].details`"
                label="Informations"
                placeholder="Informations"
                :required="false"
                :type="'multiline'"
              />
            </div>
          </div>

        </div>

      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted } from 'vue';
import { useFieldArray } from 'vee-validate';
import {IdNotDefined,ScrollToId } from '@/compositions/helper'
import TextInputComponent from '@/components/common/TextInputComponent.vue';
import SelectInputComponent from '@/components/common/SelectInputComponent.vue';
import { GetTransferTypeLabel, GetTransferDirectionLabel } from '@/compositions/helper';
import * as zod from 'zod';

const travel = defineModel<AdminTravel>({ required: true });
const validationSchema = defineModel<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>('validation-schema',{ required: false, default: () => ({ shape: {} }) });

const props = defineProps<{
  anchor?: string
}>();

const { remove: removeTransfer, push: pushTransfer, fields: transfers } = useFieldArray<TravelDbTransfer>(() => 'transfers');

const addTransfer = () => {
  pushTransfer(<TravelDbTransfer>{
    id: IdNotDefined,
    type: TravelDbTransferTypeEnum.Bus,
    name: `Transfert ${(transfers?.value.length ?? 0) + 1}`,
    direction: TravelDbTransferDirectionEnum.Outbound
  });

  ScrollToId(`transfers[${(transfers?.value.length ?? 1) - 1}]`);
}

onMounted(() => {
  validationSchema.value = {
    shape: {
      transfers: zod.array(zod.object({
        id: zod.number().nonnegative(),
        type: zod.nativeEnum(TravelDbTransferTypeEnum),
        direction: zod.nativeEnum(TravelDbTransferDirectionEnum),
        name: zod.string().min(1,'Le nom du transfert est obligatoire'),
        quota: zod.number({ message:'Le quota du transfert est obligatoire'}).nonnegative('Le quota du transfert doit être au minimum de 0'),
        meetingPoint:zod.string().optional(),
        startDate: zod.date().optional(),
        details: zod.string().optional()
      })).optional()
    },
    anchors: {
      "transfers[\\d].id": "self",
      "transfers[\\d].type": "self",
      "transfers[\\d].direction": "self",
      "transfers[\\d].name": "self",
      "transfers[\\d].quota": "self",
      "transfers[\\d].meetingPoint": "self",
      "transfers[\\d].startDate": "self",
      "transfers[\\d].details": "self"
    }
  }
});
</script>
